import {CircularProgress} from "@mui/material";
import React, {useEffect, useState} from "react";
import styles from "./ProductsGrid.module.css";
import ProductVariantDisplayer from "../ProductVariantDisplayer/ProductVariantDisplayer";
import FlatButton from "../FlatButton/FlatButton";
import ApiClient from "../../service_clients/ApiClient";
import Constants from "../../utils/Constants";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import NobiEventTracker from "../../utils/NobiEventTracker";
import Events from "../../utils/Events";
import {EntryPoint} from "../Chatbot/EntryPoint";

/**
 * ProductsGrid component that displays a grid of products from a search result
 * @param searchResultCategories {Array} The search result categories, each of which contains a list of associated products
 * @param merchant {Object} The merchant
 * @param brandTheme {Object} The brand theme
 * @param query {String} The original search query
 * @param conversationId {String} The conversation ID
 * @param structuredFilterPromptResponse {Object} The structured filter prompt response
 * @param productGridRef {Object} The reference to the product grid
 * @param productGridStyles {Object} The styles for the products grid
 * @param entryPoint {String} The entry point for the chatbot
 * @param assistantConfiguration {Object} The assistant configuration
 */
const ProductsGrid = ({
    productCategories,
    merchant,
    brandTheme,
    query,
    conversationId,
    structuredFilterPromptResponse,
    productGridRef,
    entryPoint,
    assistantConfiguration}) => {

    const [isLoadingAdditionalProductVariants, setIsLoadingAdditionalProductVariants] = useState(false);
    const [areMoreProductVariantsAvailable, setAreMoreProductVariantsAvailable] = useState(true);
    const [offset, setOffset] = useState(productCategories && productCategories.map(category => category.productVariants.length).reduce((a, b) => a + b, 0));
    const [additionalProductVariants, setAdditionalProductVariants] = useState([]);

    useEffect(() => {
        setOffset(productCategories && productCategories.map(category => category.productVariants.length).reduce((a, b) => a + b, 0));
    }, [productCategories]);

    const loadMoreProductVariants = (event) => {
        if (areMoreProductVariantsAvailable) {
            setIsLoadingAdditionalProductVariants(true);
            let eventDetails = {
                conversationId: conversationId
            };

            NobiEventTracker.trackEvent(
                merchant.id,
                entryPoint,
                Events.ClickOnShowMoreProductsButton,
                false,
                true,
                eventDetails);

            const searchDto = ApiClient.buildSearchRequestDto({
                conversationId: conversationId,
                searchQuery: query,
                limit: merchant.searchResultsToShow || Constants.SearchLimit,
                offset: offset,
                structuredFilters: structuredFilterPromptResponse
            });

            ApiClient.searchProducts(
                merchant.id,
                searchDto,
                handleNewProductVariantsReceived,
                () => setIsLoadingAdditionalProductVariants(false),
                () => setIsLoadingAdditionalProductVariants(false),
                () => setIsLoadingAdditionalProductVariants(false)
            );
        }
    }

    const handleNewProductVariantsReceived = (searchResultResponseDto) => {
        setIsLoadingAdditionalProductVariants(false);
        let categories = searchResultResponseDto?.categories;
        let productVariants = categories?.map(category => category.productVariants).flat();
        setAreMoreProductVariantsAvailable(searchResultResponseDto.areAdditionalProductsAvailable);

        if (categories && categories.length > 0) {
            setAdditionalProductVariants((prevProductVariants) => {
                const existingIds = new Set(prevProductVariants?.map(productVariant => productVariant.id));
                const uniqueNewProductVariants = productVariants.filter(productVariant => !existingIds.has(productVariant.id));
                return [...prevProductVariants, ...uniqueNewProductVariants];
            });

            setOffset(offset + productVariants.length);
        }
    }

    const renderMoreProductsButton = () => {
        if (!areMoreProductVariantsAvailable) {
            return null;
        }

        return <FlatButton
            variant="contained"
            disableElevation
            onClick={
                // We can't use the "disabled" property for this because that causes a rerender which scrolls the chatbot to the top, so we have to simulate a disabled state instead
                isLoadingAdditionalProductVariants
                    ? () => {}
                    : loadMoreProductVariants
            }
            sx={{
                backgroundColor: isLoadingAdditionalProductVariants ? "#CCC" : brandTheme?.general?.buttons?.backgroundColor,
                color: brandTheme?.general?.buttons?.textColor,
                '&:hover': {
                    backgroundColor: isLoadingAdditionalProductVariants ? "#CCC" : brandTheme?.general?.buttons?.hoverColor,
                },
                fontFamily: brandTheme?.general?.buttons?.fontFamily || "inherit",
            }}
            data-testid="show-more-button"
            id={styles.showMoreProductsButton}
        >
            {isLoadingAdditionalProductVariants && <CircularProgress size={16} style={{color: "white"}}/>}
            <div id={styles.showMoreButtonPrompt}>
                Show Similar Items <ArrowDropDownIcon />
            </div>
        </FlatButton>
    }

    function renderCategoryTitleAndCount(category) {
        return <span>
            <strong>{category.title || "Closest Matches"}</strong>
            &nbsp;
            {
                category.productVariants?.length > 0
                    ? <>({category.productVariants?.length})</>
                    : ""
            }
        </span>;
    }

    function renderCategoryProducts(category) {
        return (
            <div className={entryPoint === EntryPoint.Cart && window.innerWidth > Constants.MaxWidthForMobile ? styles.productRow : styles.productGroup} style={brandTheme?.productsGrid}>
                {category.productVariants?.map((productVariant) => (
                    <ProductVariantDisplayer
                        key={productVariant.id}
                        productVariant={productVariant}
                        brandTheme={brandTheme}
                        conversationId={conversationId}
                        merchantId={merchant.id}
                        entryPoint={entryPoint}
                        assistantConfiguration={assistantConfiguration}
                    />
                ))}
            </div>
        );
    }

    function renderCategory(category) {
        if (!category || !category.productVariants || category.productVariants?.length == 0) {
            return null;
        }

        return <div className={styles.categoryContainer} key={category.title}>
            {entryPoint && (entryPoint === EntryPoint.Cart || entryPoint === EntryPoint.RespondingFromCartAssistant) && renderCategoryTitleAndCount(category)}
            {renderCategoryProducts(category)}
        </div>;
    }

    function renderProductGrid() {
        let showMoreCategory = null;
        if (additionalProductVariants && additionalProductVariants.length > 0) {
            showMoreCategory = {
                name: "Additional Products",
                productVariants: additionalProductVariants
            };
        }

        return <div id={styles.productSearchResults} ref={productGridRef} style={entryPoint === EntryPoint.Cart ? {marginLeft: "-0.5em"} : {}}>
            {productCategories?.map((category) => renderCategory(category))}
            {renderCategory(showMoreCategory)}
        </div>;
    }

    return <div id={styles.productsGridContainer} className={entryPoint === EntryPoint.Cart ? styles.cartContainer : null}>
        {renderProductGrid()}
        {(entryPoint === null || (entryPoint !== EntryPoint.Cart && entryPoint !== EntryPoint.RespondingFromCartAssistant)) && renderMoreProductsButton()}
    </div>
}

export default ProductsGrid;